import { createEffect, createMemo, untrack } from "solid-js"
import { Mesh, type MeshBasicMaterial, type MeshLambertMaterial, MeshStandardMaterial } from "three"
import { onCleanupDispose } from "../../../../rx/cleanup/onCleanupDispose"
import { setDebugImageList } from "../../../../rx/signal/useSharedDebugImageListSignal"
import type { Engine } from "../../../core/engine/Engine.type"
import type { EntityId } from "../../../core/entity/EntityId.type"
import entityTypeMetaList from "../../../core/entity/entityTypeMetaList.generated"
import shouldEntityShowFadedFrames from "../../../core/entity/shouldEntityShowFadedFrames"
import findByIdOrThrow from "../../../core/findByIdOrThrow"
import type { HasHP } from "../../../core/has_hp"
import { samePosition } from "../../../core/tile_position_xy/samePosition"
import createDebugRenderTargetGetOffscreenCanvasFunction from "../../../image/createDebugRenderTargetGetOffscreenCanvasFunction"
import type { EntityThreeModel } from "../../EntityThreeModel/EntityThreeModel.type"
import runWithObject3DOwner from "../../fn/runWithObject3DOwner"
import useEngineTextureWriter from "../../fn/useEngineTextureWriter"
import { TextureSizeSettingId } from "../../objects/TerrainMesh1V3/TileFloor/Texture/TextureSizeSettingId.enum"
import createTextureWriterWebGLRenderTarget from "../createTextureWriterWebGLRenderTarget"
import createStandardUnitAOMapShaderMaterial from "./createStandardUnitAOMapShaderMaterial"
import createStandardUnitMetalnessMapShaderMaterial from "./createStandardUnitMetalnessMapShaderMaterial"
import createStandardUnitNormalMapShaderMaterial from "./createStandardUnitNormalMapShaderMaterial"
import createStandardUnitRoughnessMapShaderMaterial from "./createStandardUnitRoughnessMapShaderMaterial"
import createStandardUnitTextureShaderMaterial from "./createStandardUnitTextureShaderMaterial"

// 0 to 1.0
const DEBUG_PERCENT_LOST_HP: number = 0.9
const SHOW_DEBUG_IMAGES = false

const cache = new WeakMap<NonNullable<EntityThreeModel['obj']>, boolean>

// placeholder signal accessor
function getTextureSizeSetting() {
  // return TextureSizeSettingId.XL
  return TextureSizeSettingId.LG
}

export default function createStandardUnitTextureEffect(
  engine: Engine,
  object: NonNullable<EntityThreeModel['obj']>,
  entNode: EntityThreeModel['node']
) {
  // console.log('createStandardUnitTextureEffect', entNode.id)
  if (!cache.has(object)) {
    const entityId: EntityId = entNode.id
    const entityTypeMeta = findByIdOrThrow(entityTypeMetaList, entNode.etype_id)
    runWithObject3DOwner(object, () => {
      const [getTextureWriter] = useEngineTextureWriter(engine)
      const textureWriter = getTextureWriter()
      const diffuseRenderTarget = createTextureWriterWebGLRenderTarget(getTextureSizeSetting())
      onCleanupDispose(diffuseRenderTarget)
      const normalRenderTarget = createTextureWriterWebGLRenderTarget(getTextureSizeSetting())
      onCleanupDispose(normalRenderTarget)
      const roughnessRenderTarget = createTextureWriterWebGLRenderTarget(getTextureSizeSetting())
      onCleanupDispose(roughnessRenderTarget)
      const metalnessRenderTarget = createTextureWriterWebGLRenderTarget(getTextureSizeSetting())
      onCleanupDispose(metalnessRenderTarget)
      const aoRenderTarget = createTextureWriterWebGLRenderTarget(getTextureSizeSetting())
      onCleanupDispose(aoRenderTarget)
      // const bumpRenderTarget = createTextureWriterWebGLRenderTarget(getTextureSizeSetting())
      // onCleanupDispose(bumpRenderTarget)
      // const displacementRenderTarget = createTextureWriterWebGLRenderTarget(getTextureSizeSetting())
      // onCleanupDispose(displacementRenderTarget)

      const shaderMaterial = createStandardUnitTextureShaderMaterial()
      const normalShaderMaterial = createStandardUnitNormalMapShaderMaterial()
      const roughnessShaderMaterial = createStandardUnitRoughnessMapShaderMaterial()
      const metalnessShaderMaterial = createStandardUnitMetalnessMapShaderMaterial()
      const aoShaderMaterial = createStandardUnitAOMapShaderMaterial()
      // const bumpShaderMaterial = createStandardUnitBumpMapShaderMaterial()
      // const displacementShaderMaterial = createStandardUnitDisplacementMapShaderMaterial()

      const { u_lost_hp, u_active, u_seed } = shaderMaterial.uniforms
      const { u_lost_hp: u_lost_hp_normal, u_seed: u_seed_normal } = normalShaderMaterial.uniforms
      const { u_lost_hp: u_lost_hp_roughness, u_seed: u_seed_roughness } = roughnessShaderMaterial.uniforms
      const { u_lost_hp: u_lost_hp_metalness, u_seed: u_seed_metalness } = metalnessShaderMaterial.uniforms
      const { u_lost_hp: u_lost_hp_ao, u_seed: u_seed_ao } = aoShaderMaterial.uniforms
      // const { u_lost_hp: u_lost_hp_bump, u_seed: u_seed_bump } = bumpShaderMaterial.uniforms
      // const { u_lost_hp: u_lost_hp_displacement, u_seed: u_seed_displacement } = displacementShaderMaterial.uniforms

      u_seed.value = entityId
      u_seed_normal.value = entityId
      u_seed_roughness.value = entityId
      u_seed_metalness.value = entityId
      u_seed_ao.value = entityId
      // u_seed_bump.value = entityId
      // u_seed_displacement.value = entityId

      const maxHp = (entityTypeMeta.entDefaults as HasHP).hp
      const percentLostHp = createMemo(() => {
        const hp = entNode.raw().hp || 0
        let percentLostHp = (maxHp - hp) / maxHp
        // enforce a minimum because I like a bit of grit
        percentLostHp = Math.floor(10 * Math.max(0.1, percentLostHp) / 10)
        return percentLostHp
      })

      const isActive = createMemo(() => {
        return shouldEntityShowFadedFrames(entNode.raw())
      })

      createEffect(() => {
        const textureSize = getTextureSizeSetting()
        diffuseRenderTarget.setSize(textureSize, textureSize)
        normalRenderTarget.setSize(textureSize, textureSize)
        roughnessRenderTarget.setSize(textureSize, textureSize)
        metalnessRenderTarget.setSize(textureSize, textureSize)
        aoRenderTarget.setSize(textureSize, textureSize)
        // bumpRenderTarget.setSize(textureSize, textureSize)
        // displacementRenderTarget.setSize(textureSize, textureSize)

        createEffect(() => {
          u_lost_hp.value = percentLostHp()
          u_lost_hp_normal.value = percentLostHp()
          u_lost_hp_roughness.value = percentLostHp()
          u_lost_hp_metalness.value = percentLostHp()
          u_lost_hp_ao.value = percentLostHp()
          // u_lost_hp_bump.value = percentLostHp()
          // u_lost_hp_displacement.value = percentLostHp()
          u_active.value = isActive()

          // 0 to 1.0
          if (DEBUG_PERCENT_LOST_HP >= 0) {
            u_lost_hp.value = DEBUG_PERCENT_LOST_HP
            u_lost_hp_normal.value = DEBUG_PERCENT_LOST_HP
            u_lost_hp_roughness.value = DEBUG_PERCENT_LOST_HP
            u_lost_hp_metalness.value = DEBUG_PERCENT_LOST_HP
            u_lost_hp_ao.value = DEBUG_PERCENT_LOST_HP
            // u_lost_hp_bump.value = DEBUG_PERCENT_LOST_HP
            // u_lost_hp_displacement.value = DEBUG_PERCENT_LOST_HP
          }

          // Render all texture maps
          textureWriter.render(shaderMaterial, diffuseRenderTarget)
          textureWriter.render(normalShaderMaterial, normalRenderTarget)
          textureWriter.render(roughnessShaderMaterial, roughnessRenderTarget)
          textureWriter.render(metalnessShaderMaterial, metalnessRenderTarget)
          textureWriter.render(aoShaderMaterial, aoRenderTarget)
          // textureWriter.render(bumpShaderMaterial, bumpRenderTarget)
          // textureWriter.render(displacementShaderMaterial, displacementRenderTarget)

          if (SHOW_DEBUG_IMAGES) {
            if (samePosition(untrack(entNode.raw), { x: 18, y: 1 })) {
              // Set names on the textures for reference
              diffuseRenderTarget.texture.name = 'diffuseMap'
              normalRenderTarget.texture.name = 'normalMap'
              roughnessRenderTarget.texture.name = 'roughnessMap'
              metalnessRenderTarget.texture.name = 'metalnessMap'
              aoRenderTarget.texture.name = 'aoMap'
              // bumpRenderTarget.texture.name = 'bumpMap';
              // displacementRenderTarget.texture.name = 'displacementMap';

              // Create debug images using texture names from render targets
              const renderTargets = [
                diffuseRenderTarget,
                normalRenderTarget,
                roughnessRenderTarget,
                metalnessRenderTarget,
                aoRenderTarget,
                // bumpRenderTarget,
                // displacementRenderTarget,
              ]

              setDebugImageList(renderTargets.map(renderTarget => ({
                img: createDebugRenderTargetGetOffscreenCanvasFunction(engine, renderTarget, renderTarget.texture)(),
                name: renderTarget.texture.name
              })))
            }
          }
        })
      })

      object.traverse((child) => {
        if (child instanceof Mesh) {
          const material = child.material as MeshBasicMaterial | MeshStandardMaterial | MeshLambertMaterial

          // // Fix blockiness
          // this will be done in the make function
          // if (isEntityInfantry(entNode)) {
          //   child.geometry = toCreasedNormals(child.geometry)
          // }

          if (material.name == 'Window' || material.userData.W) {
            // do nothing
          } else if (material.name === 'Black') {
            // do nothing
          } else if (child.name === 'TrackWheelRim1') {
            // don't camo a wheel (MediumRockets)
          } else if (child.name === 'WheelRim2') {
            // don't camo a wheel (LightRecon)
          } else if (child.name === 'Tracks_1' || child.name === 'Tracks_2') {
            // don't camo a wheel (APC)
          } else {
            material.map = diffuseRenderTarget.texture
            // MeshStandardMaterial and MeshLambertMaterial support normal and AO maps
            if ('normalMap' in material) {
              material.normalMap = normalRenderTarget.texture
              material.aoMap = aoRenderTarget.texture
              // Bump map is supported by both
              // material.bumpMap = normalRenderTarget.texture
              // material.bumpScale = 0.02 // Subtle bump effect
            }
            // Only MeshStandardMaterial supports roughness, metalness, and displacement maps
            if ('roughnessMap' in material) {
              material.roughnessMap = roughnessRenderTarget.texture
              material.metalnessMap = metalnessRenderTarget.texture
              // material.displacementMap = displacementRenderTarget.texture
              // material.displacementScale = 0.05 // Subtle displacement
              // material.displacementBias = -0.025 // Center the displacement
            }

            // assigning materials new textures needs this
            material.needsUpdate = true
          }
        }
      })
    })
    cache.set(object, true)
  }
}